export type BoneRefBuildItem = {
  index: number;
  parentIndex: number | null;
  name: string;
};

export type BoneRefBuildResult = {
  shortRefByIndex: string[];
  fullPathRefByIndex: string[];
  canonicalRefByIndex: string[];
  nameCounts: Map<string, number>;
  uniqueNameToIndex: Map<string, number>;
  refToIndex: Map<string, number>;
};

const ROOT_PARENT_KEY = '__root__';
const SIBLING_ORDINAL_DELIMITER = '~';

function siblingKey(parentIndex: number | null, name: string): string {
  return `${parentIndex ?? ROOT_PARENT_KEY}\u001f${name}`;
}

export function isBoneRefShortSafe(name: string): boolean {
  return !/[\\/]/.test(name);
}

function escapePathSegment(name: string): string {
  return name
    .replace(/\\/g, '\\\\')
    .replace(/\//g, '\\/')
    .replace(/~/g, '\\~');
}

export function buildBoneRefData(items: BoneRefBuildItem[]): BoneRefBuildResult {
  const shortRefByIndex: string[] = [];
  const fullPathRefByIndex: string[] = [];
  const canonicalRefByIndex: string[] = [];

  const nameCounts = new Map<string, number>();
  for (const item of items) {
    nameCounts.set(item.name, (nameCounts.get(item.name) ?? 0) + 1);
    shortRefByIndex[item.index] = item.name;
  }

  const uniqueNameToIndex = new Map<string, number>();
  for (const item of items) {
    if ((nameCounts.get(item.name) ?? 0) === 1) {
      uniqueNameToIndex.set(item.name, item.index);
    }
  }

  const itemByIndex = new Map<number, BoneRefBuildItem>();
  const siblingTotals = new Map<string, number>();
  for (const item of items) {
    itemByIndex.set(item.index, item);
    const key = siblingKey(item.parentIndex, item.name);
    siblingTotals.set(key, (siblingTotals.get(key) ?? 0) + 1);
  }

  const siblingSeen = new Map<string, number>();
  const pathSegmentByIndex = new Map<number, string>();
  for (const item of items) {
    const key = siblingKey(item.parentIndex, item.name);
    const seen = (siblingSeen.get(key) ?? 0) + 1;
    siblingSeen.set(key, seen);
    const escaped = escapePathSegment(item.name);
    const needsOrdinal = (siblingTotals.get(key) ?? 0) > 1;
    pathSegmentByIndex.set(
      item.index,
      needsOrdinal ? `${escaped}${SIBLING_ORDINAL_DELIMITER}${seen}` : escaped,
    );
  }

  const fullPathCache = new Map<number, string>();
  const building = new Set<number>();
  const buildFullPath = (index: number): string => {
    const cached = fullPathCache.get(index);
    if (cached != null) return cached;

    const item = itemByIndex.get(index);
    const segment = pathSegmentByIndex.get(index) ?? '';
    if (!item) {
      fullPathCache.set(index, segment);
      return segment;
    }

    if (building.has(index)) {
      fullPathCache.set(index, segment);
      return segment;
    }
    building.add(index);

    let path = segment;
    if (item.parentIndex !== null && itemByIndex.has(item.parentIndex)) {
      const parentPath = buildFullPath(item.parentIndex);
      if (parentPath.length > 0) {
        path = `${parentPath}/${segment}`;
      }
    }

    building.delete(index);
    fullPathCache.set(index, path);
    return path;
  };

  for (const item of items) {
    const fullPathRef = buildFullPath(item.index);
    fullPathRefByIndex[item.index] = fullPathRef;
    const shortIsUsable =
      (nameCounts.get(item.name) ?? 0) === 1 && isBoneRefShortSafe(item.name);
    canonicalRefByIndex[item.index] = shortIsUsable ? item.name : fullPathRef;
  }

  const refToIndex = new Map<string, number>();
  for (const item of items) {
    const index = item.index;
    const fullPathRef = fullPathRefByIndex[index];
    if (fullPathRef.length > 0 && !refToIndex.has(fullPathRef)) {
      refToIndex.set(fullPathRef, index);
    }

    const shortRef = item.name;
    const shortIsUsable =
      (nameCounts.get(shortRef) ?? 0) === 1 && isBoneRefShortSafe(shortRef);
    if (shortIsUsable && !refToIndex.has(shortRef)) {
      refToIndex.set(shortRef, index);
    }
  }

  return {
    shortRefByIndex,
    fullPathRefByIndex,
    canonicalRefByIndex,
    nameCounts,
    uniqueNameToIndex,
    refToIndex,
  };
}
